import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { Loading } from '@carvana/purchase-styles';
import { PdfErrorAlert } from './alert';
import Pagination from './pagination';
import * as sc from './pdf-viewer.styles';

// react-pdf 9.x — requires explicit worker configuration
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PdfViewer = ({ file }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfPageCount, setPdfPageCount] = useState(null);

  const inputRef = useRef(null);

  const onLoadSuccess = useCallback(pdfResult => {
    const { numPages } = pdfResult;

    if (numPages) {
      setPdfPageCount(numPages);
    }
  }, []);

  const onLoadError = useCallback(error => console.error('Failed to load PDF document', error), []);

  const onSourceError = useCallback(
    error => console.error('Failed to retrieve PDF document source', error),
    []
  );

  const handlePreviousClick = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const handleNextClick = useCallback(() => {
    if (currentPage < pdfPageCount) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, pdfPageCount]);

  return (
    <sc.Wrapper>
      <sc.PdfWrapper ref={inputRef}>
        <sc.StyledDocument
          file={file}
          loading={<Loading />}
          error={<PdfErrorAlert />}
          onLoadSuccess={onLoadSuccess}
          onLoadError={onLoadError}
          onSourceError={onSourceError}
        >
          {Array.from(new Array(pdfPageCount), (_el, index) => (
            <sc.StyledPage
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              scale={0.9}
              width={inputRef.current?.clientWidth}
              height={inputRef.current?.clientHeight}
              className={currentPage === index + 1 ? 'active' : 'inactive'}
            />
          ))}
        </sc.StyledDocument>
      </sc.PdfWrapper>
      {pdfPageCount && (
        <sc.PaginationWrapper>
          <Pagination
            currentPage={currentPage}
            pdfPageCount={pdfPageCount}
            onNextClick={handleNextClick}
            onPrevClick={handlePreviousClick}
          />
        </sc.PaginationWrapper>
      )}
    </sc.Wrapper>
  );
};

PdfViewer.propTypes = {
  file: PropTypes.string.isRequired
};

export default PdfViewer;
